import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Custom hook for infinite scroll functionality using Intersection Observer API
 *
 * @param options Configuration options for infinite scroll
 * @returns A ref to attach to the trigger element (usually at the bottom of the list)
 */
export const useInfiniteScroll = ({ hasNextPage, isFetchingNextPage, fetchNextPage, rootMargin = '100px', threshold = 0.1, }) => {
    const [isMounted, setIsMounted] = useState(false);
    const observerRef = useRef(null);
    const triggerRef = useRef(null);
    const handleIntersection = useCallback((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && isMounted) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, isMounted]);
    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);
    useEffect(() => {
        const currentTriggerRef = triggerRef.current;
        if (!currentTriggerRef || !isMounted) {
            return;
        }
        // Create intersection observer
        observerRef.current = new IntersectionObserver(handleIntersection, {
            rootMargin,
            threshold,
        });
        // Start observing
        observerRef.current.observe(currentTriggerRef);
        // Cleanup function
        return () => {
            if (observerRef.current && currentTriggerRef) {
                observerRef.current.unobserve(currentTriggerRef);
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleIntersection, rootMargin, threshold, isMounted]);
    return triggerRef;
};
